import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Image, Plus, Trash2, Copy, ExternalLink, Search, Loader2 } from "lucide-react";

export default function MediaPage() {
  const { token } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [newMedia, setNewMedia] = useState({ name: "", url: "", type: "image", size: 0 });
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await fetch(`${API}/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMedia(data);
      }
    } catch (error) {
      console.error("Failed to fetch media:", error);
      toast.error("Erreur lors du chargement des médias");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    
    try {
      const response = await fetch(`${API}/media`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newMedia)
      });
      
      if (response.ok) {
        toast.success("Média ajouté");
        setNewMedia({ name: "", url: "", type: "image", size: 0 });
        setShowAdd(false);
        fetchMedia();
      } else {
        toast.error("Erreur lors de l'ajout");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await fetch(`${API}/media/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Média supprimé");
        fetchMedia();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiée");
  };

  const filteredMedia = media.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="media-loading">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="media-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Bibliothèque média</h1>
          <p className="text-muted-foreground mt-1">Gérez vos images et fichiers</p>
        </div>
        <Button 
          className="bg-primary hover:bg-primary/90" 
          onClick={() => setShowAdd(true)}
          data-testid="add-media-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un média
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un média..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-media-input"
        />
      </div>

      {/* Add Media Form */}
      {showAdd && (
        <Card className="border border-border/60" data-testid="add-media-form">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Ajouter un média</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="media-name">Nom</Label>
                  <Input
                    id="media-name"
                    placeholder="Mon image"
                    value={newMedia.name}
                    onChange={(e) => setNewMedia({ ...newMedia, name: e.target.value })}
                    required
                    data-testid="media-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="media-url">URL</Label>
                  <Input
                    id="media-url"
                    placeholder="https://exemple.com/image.jpg"
                    value={newMedia.url}
                    onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value })}
                    required
                    data-testid="media-url-input"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={adding}>
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Media Grid */}
      {filteredMedia.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredMedia.map((item) => (
            <Card 
              key={item.id} 
              className="border border-border/60 overflow-hidden group"
              data-testid={`media-card-${item.id}`}
            >
              <div className="aspect-square relative bg-muted">
                <img
                  src={item.url}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-full items-center justify-center">
                  <Image className="w-12 h-12 text-muted-foreground/50" />
                </div>
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button 
                    size="icon" 
                    variant="secondary" 
                    className="h-8 w-8"
                    onClick={() => copyUrl(item.url)}
                    data-testid={`copy-media-${item.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="secondary" 
                    className="h-8 w-8"
                    onClick={() => window.open(item.url, "_blank")}
                    data-testid={`view-media-${item.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="destructive" 
                    className="h-8 w-8"
                    onClick={() => setDeleteId(item.id)}
                    data-testid={`delete-media-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-medium truncate">{item.name}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(item.created_at).toLocaleDateString('fr-FR')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Image className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">
              {search ? "Aucun résultat" : "Bibliothèque vide"}
            </h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              {search 
                ? "Aucun média ne correspond à votre recherche"
                : "Ajoutez des images pour les utiliser dans vos articles"
              }
            </p>
            {!search && (
              <Button 
                className="bg-primary hover:bg-primary/90" 
                onClick={() => setShowAdd(true)}
                data-testid="empty-add-media-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un média
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce média ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le média sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
